import { useContext } from 'react';
import SectionContext from '../../../../SectionContext';
import FieldContext from '../../../FieldContext';
import sortByPosition from '@/lib/utils/sorting/sortByPosition';
import ContentGroupType from '@/lib/constants/challengeModel/ContentGroupType.enum';
import ModelsContext from '../../../../../../ModelsContext';
import useApi from '@/lib/api/useApi';
import { type EditorFieldWithoutRelations } from '@/lib/types';
import SelectedModelIdContext from '@/pages/EditorPage/ChallengeModelsEditor/SelectedModelIdContext';

const useCurrentFieldSectionMoving = (): [
  () => Promise<void>,
  () => Promise<void>,
] => {
  const api = useApi();

  const [models, setModels] = useContext(ModelsContext);
  const selectedModelId = useContext(SelectedModelIdContext);

  const allFields = models[selectedModelId].fields;

  const { previousSection, section, nextSection } = useContext(SectionContext);
  const { field } = useContext(FieldContext);

  const updateSectionInModelsState = (
    newSectionId: number,
    updatedField: EditorFieldWithoutRelations,
  ): void => {
    setModels(models => {
      const model = models[selectedModelId];

      model.fields[field!.id] = {
        ...model.fields[field!.id],
        ...updatedField,
      };

      const oldSection = model.sections[section.id];

      oldSection.fields.splice(
        oldSection.fields.findIndex(({ id }) => id === field!.id),
        1,
      );

      model.sections[newSectionId].fields.push({ id: field!.id });
    });
  };

  const moveCurrentFieldToPreviousSection = async (): Promise<void> => {
    if (previousSection === null) {
      return;
    }

    const previousSectionFields = previousSection.fields
      .map(({ id }) => allFields[id])
      .filter(({ contentGroup }) => contentGroup === ContentGroupType.BOTTOM)
      .sort(sortByPosition);
    const previousSectionFieldsHighestPosition =
      previousSectionFields.reverse()[0]?.position ?? 0;

    const newFieldPosition = previousSectionFieldsHighestPosition + 1;

    const updatedField = await api.post<EditorFieldWithoutRelations>(
      `editor/challenge-model/fields/${field!.id}/position/set-section`,
      {
        sectionId: previousSection.id,
        contentGroup: ContentGroupType.BOTTOM,
        position: newFieldPosition,
      },
    );

    updateSectionInModelsState(previousSection.id, updatedField);
  };

  const moveCurrentFieldToNextSection = async (): Promise<void> => {
    if (nextSection === null) {
      return;
    }

    const nextSectionFields = nextSection.fields
      .map(({ id }) => allFields[id])
      .filter(({ contentGroup }) => contentGroup === ContentGroupType.LEFT)
      .sort(sortByPosition);
    const nextSectionFieldsLowestPosition = nextSectionFields[0]?.position ?? 2;

    const newFieldPosition = nextSectionFieldsLowestPosition / 2;

    const updatedField = await api.post<EditorFieldWithoutRelations>(
      `editor/challenge-model/fields/${field!.id}/position/set-section`,
      {
        sectionId: nextSection.id,
        contentGroup: ContentGroupType.LEFT,
        position: newFieldPosition,
      },
    );

    updateSectionInModelsState(nextSection.id, updatedField);
  };

  return [moveCurrentFieldToPreviousSection, moveCurrentFieldToNextSection];
};

export default useCurrentFieldSectionMoving;
